import classNames from "classnames";
import {
  Registration,
  RegistrationStatus,
} from "store/apis/salesTaxRegistration";
import { pluralize } from "utils/pluralize";
import { StatusMap } from "./SalesTaxRegistrations";

type RegistrationsOverviewCardProps = {
  registrations: Registration[];
};

type OverviewSegment = {
  status: RegistrationStatus;
  count: number;
  dotClassName: string;
};

const SEGMENT_DOT_CLASSNAME: Record<RegistrationStatus, string> = {
  PENDING_DETAILS: "t-bg-red",
  IN_REVIEW: "t-bg-blue-50",
  PROCESSING: "t-bg-orange-40",
  VALIDATING: "t-bg-blue-50",
  FILING_IN_PROGRESS: "t-bg-orange-40",
  FILING_INFO_MISSING: "t-bg-orange-40",
  REGISTERED: "t-bg-dark_green-50",
  NOT_STARTED: "t-bg-red",
};

const getSegments = (registrations: Registration[]): OverviewSegment[] => {
  const countsByStatus: Partial<Record<RegistrationStatus, number>> = {};
  registrations.forEach((registration) => {
    countsByStatus[registration.status] =
      (countsByStatus[registration.status] || 0) + 1;
  });

  return (Object.keys(countsByStatus) as RegistrationStatus[]).map(
    (status) => ({
      status,
      count: countsByStatus[status] || 0,
      dotClassName: SEGMENT_DOT_CLASSNAME[status],
    })
  );
};

export const RegistrationsOverviewCard = ({
  registrations,
}: RegistrationsOverviewCardProps) => {
  const totalCount = registrations.length;
  const segments = getSegments(registrations);

  return (
    <div className="t-flex-1 t-rounded-lg t-border t-border-solid t-border-neutral-0 t-p-5">
      <div className="t-flex t-flex-col t-gap-4">
        <div>
          <p className="t-m-0 t-text-body t-text-text-30">Overview</p>
          <p className="t-mt-1 t-m-0 t-text-title-h2 t-text-text-100">
            {totalCount}{" "}
            <span className="t-text-subtext-sm t-font-normal t-text-text-30">
              {pluralize(totalCount, "registration", "registrations", false)}
            </span>
          </p>
        </div>

        <div className="t-flex t-flex-wrap t-items-center t-gap-4">
          {segments
            .filter((segment) => segment.count > 0)
            .map((segment) => (
              <span
                key={segment.status}
                className="t-flex t-items-center t-gap-1.5 t-text-body-sm t-text-text-60"
              >
                <span
                  className={classNames(
                    "t-h-1.5 t-w-1.5 t-rounded-full",
                    segment.dotClassName
                  )}
                />
                {segment.count} {StatusMap[segment.status].toLowerCase()}
              </span>
            ))}
        </div>
      </div>
    </div>
  );
};
